import { apiRequest, API_BASE_URL } from '../config/api';

/**
 * User Management Service
 * Handles all user and admin management API calls
 */

class UserManagementService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // =============================================================================
  // CLIENT USER MANAGEMENT
  // =============================================================================

  /**
   * Get all client users
   */
  async getAllUsers(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/api/admin/users/?${queryString}`
        : '/api/admin/users/';

      const response = await apiRequest(endpoint, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Get user details by ID
   */
  async getUserDetail(userId) {
    try {
      const response = await apiRequest(`/api/admin/users/${userId}/`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching user detail:', error);
      throw error;
    }
  }

  /**
   * Create new client user
   */
  async createUser(userData) {
    try {
      const response = await apiRequest('/api/admin/users/create/', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update client user
   */
  async updateUser(userId, userData) {
    try {
      const response = await apiRequest(`/api/admin/users/${userId}/update/`, {
        method: 'PATCH',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Delete client user
   */
  async deleteUser(userId) {
    try {
      const response = await apiRequest(`/api/admin/users/${userId}/delete/`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // =============================================================================
  // ADMIN USER MANAGEMENT
  // =============================================================================

  /**
   * Get all admin users
   */
  async getAllAdmins(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/api/admin/admins/?${queryString}`
        : '/api/admin/admins/';

      const response = await apiRequest(endpoint, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching admins:', error);
      throw error;
    }
  }

  /**
   * Get admin details by ID
   */
  async getAdminDetail(adminId) {
    try {
      const response = await apiRequest(`/api/admin/admins/${adminId}/`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching admin detail:', error);
      throw error;
    }
  }

  /**
   * Create new admin user
   */
  async createAdmin(adminData) {
    try {
      const response = await apiRequest('/api/admin/admins/create/', {
        method: 'POST',
        body: JSON.stringify(adminData),
      });
      return response;
    } catch (error) {
      console.error('Error creating admin:', error);
      throw error;
    }
  }

  /**
   * Update admin user
   */
  async updateAdmin(adminId, adminData) {
    try {
      const response = await apiRequest(
        `/api/admin/admins/${adminId}/update/`,
        {
          method: 'PATCH',
          body: JSON.stringify(adminData),
        },
      );
      return response;
    } catch (error) {
      console.error('Error updating admin:', error);
      throw error;
    }
  }

  /**
   * Delete admin user
   */
  async deleteAdmin(adminId) {
    try {
      const response = await apiRequest(
        `/api/admin/admins/${adminId}/delete/`,
        {
          method: 'DELETE',
        },
      );
      return response;
    } catch (error) {
      console.error('Error deleting admin:', error);
      throw error;
    }
  }

  // =============================================================================
  // STATISTICS
  // =============================================================================

  /**
   * Get user statistics
   */
  async getUserStats() {
    try {
      const response = await apiRequest('/api/admin/users/stats/', {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const userManagementService = new UserManagementService();
export default userManagementService;
